import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Typography,
  Box,
  Checkbox,
  InputAdornment,
  Radio,
  RadioGroup,
  IconButton,
} from '@mui/material';
import { Map as MapIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CreateContratoServicosData } from '../../services/contratoServicosService';
import { planosService } from '../../services/planosService';
import PlanosSelect from '../selects/PlanosSelect';
import SelectEstadosBr from '../selects/EstadosBrSelect';
import NasSelect from '../selects/NasSelect';
import ClassFiscalSelect from '../selects/ClassFiscalSelect';
import BairroSelect from '../selects/BairroSelect';
import { Planos } from '../../types';
import { formatarIp, formatarIpOnInput, formatarMacAddress, formatarMacAddressOnInput, formatarValor, formatarValorOnInput } from '../../utils/formatToView';
import { MapSelectorModal } from '../common';

interface ContratoServicosIntFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateContratoServicosData) => void;
  editingServico: any;
  submitting: boolean;
  escritorio_id?: number; // ID do escritório para filtrar planos
  contractAddress?: {
    cep_inst?: string;
    cidade_inst?: string;
    estado_inst?: string;
    bairro_inst?: string;
    endereco_inst?: string;
    numero_inst?: string;
    complemento_inst?: string;
    coordenadas_inst?: string;
    referencia_inst?: string;
  };
}

const ContratoServicosIntForm: React.FC<ContratoServicosIntFormProps> = ({
  open,
  onClose,
  onSubmit,
  editingServico,
  submitting,
  escritorio_id,
  contractAddress
}) => {
  const [formData, setFormData] = React.useState<CreateContratoServicosData>({
    tipo: 'internet',
    status: 'aguardando_instalacao',
    acessos_simultaneos: false,
    valor: 0.00,
    desconto: 0,
    acrescimo: 0,
    isento: false,
    tipo_meio: 'fibra',
    tipo_atendimento: 'urbano',
    gera_dici: true,
    codigo_municipio: '',
    tipo_moradia: undefined,
  });
  const [useContractAddress, setUseContractAddress] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<{ [key: string]: string }>({});

  // Estados para modal do mapa
  const [mapModalOpen, setMapModalOpen] = React.useState(false);
  const [selectedLocation, setSelectedLocation] = React.useState<{ lat: number, lng: number } | null>(null);

  // Estados para valores formatados em moeda
  const [valorFormatado, setValorFormatado] = React.useState('0,00');
  const [acrescimoFormatado, setAcrescimoFormatado] = React.useState('0,00');
  const [descontoFormatado, setDescontoFormatado] = React.useState('0,00');

  // Função para formatar valor para moeda brasileira
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  // Inicializar formData quando o modal abrir ou editingServico mudar
  React.useEffect(() => {
    if (open) {
      if (editingServico) {
        // Modo edição - preencher com dados do serviço
        const valorNum = Number(editingServico.valor) || 0;
        const acrescimoNum = Number(editingServico.acrescimo) || 0;
        const descontoNum = Number(editingServico.desconto) || 0;

        setFormData({
          tipo: editingServico.tipo || 'internet',
          status: editingServico.status || 'aguardando_instalacao',
          plano_id: editingServico.plano_id || 0,
          acessos_simultaneos: editingServico.acessos_simultaneos || false,
          nas_server: editingServico.nas_server || '',
          usuario_pppoe: editingServico.usuario_pppoe || '',
          senha_pppoe: editingServico.senha_pppoe || '',
          endereco_ip: editingServico.endereco_ip || '',
          endereco_mac: editingServico.endereco_mac || '',
          pool: editingServico.pool || '',
          valor: valorNum,
          desconto: descontoNum,
          acrescimo: acrescimoNum,
          cep_inst: editingServico.cep_inst || '',
          cidade_inst: editingServico.cidade_inst || '',
          estado_inst: editingServico.estado_inst || '',
          bairro_inst: editingServico.bairro_inst || '',
          endereco_inst: editingServico.endereco_inst || '',
          numero_inst: editingServico.numero_inst || '',
          complemento_inst: editingServico.complemento_inst || '',
          codigo_municipio: editingServico.codigo_municipio || '',
          coordenadas_inst: editingServico.coordenadas_inst || '',
          referencia_inst: editingServico.referencia_inst || '',
          observacoes: editingServico.observacoes || '',
          cclass_fiscal: editingServico.cclass_fiscal || undefined,
          isento: editingServico.isento || false,
          tipo_meio: editingServico.tipo_meio || 'fibra',
          tipo_atendimento: editingServico.tipo_atendimento || 'urbano',
          gera_dici: editingServico.gera_dici !== undefined ? editingServico.gera_dici : true,
          tipo_moradia: editingServico.tipo_moradia || undefined,
        });

        // Formatar valores para exibição
        setValorFormatado(formatarMoeda(valorNum));
        setAcrescimoFormatado(formatarMoeda(acrescimoNum));
        setDescontoFormatado(formatarMoeda(descontoNum));
      } else {
        // Modo criação - valores padrão
        setFormData({
          tipo: 'internet',
          status: 'aguardando_instalacao',
          acessos_simultaneos: false,
          valor: 0.00,
          desconto: 0,
          acrescimo: 0,
          isento: false,
          tipo_meio: 'fibra',
          tipo_atendimento: 'urbano',
          gera_dici: true,
          tipo_moradia: undefined,
        });

        // Resetar valores formatados
        setValorFormatado('0,00');
        setAcrescimoFormatado('0,00');
        setDescontoFormatado('0,00');
      }
      // Limpar erros de validação
      setValidationErrors({});
      setUseContractAddress(false);
    }
  }, [open, editingServico]);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Campos obrigatórios
    if (!formData.plano_id || formData.plano_id === 0) {
      errors.plano_id = 'Plano é obrigatório';
    }

    if (!formData.usuario_pppoe || formData.usuario_pppoe.trim() === '') {
      errors.usuario_pppoe = 'Usuário PPPoE é obrigatório';
    }

    if (!formData.nas_server) {
      errors.nas_server = 'Servidor NAS é obrigatório';
    }

    if (!formData.senha_pppoe || formData.senha_pppoe.trim() === '') {
      errors.senha_pppoe = 'Senha PPPoE é obrigatória';
    }

    if (!formData.valor || formData.valor <= 0.00) {
      errors.valor = 'Valor é obrigatório e deve ser maior que zero';
    }

    if (!formData.cep_inst || formData.cep_inst.trim() === '') {
      errors.cep_inst = 'CEP é obrigatório';
    }

    if (!formData.endereco_inst || formData.endereco_inst.trim() === '') {
      errors.endereco_inst = 'Endereço é obrigatório';
    }

    if (!formData.bairro_inst || formData.bairro_inst.trim() === '') {
      errors.bairro_inst = 'Bairro é obrigatório';
    }

    if (!formData.cidade_inst || formData.cidade_inst.trim() === '') {
      errors.cidade_inst = 'Cidade é obrigatória';
    }

    if (!formData.estado_inst || formData.estado_inst.trim() === '') {
      errors.estado_inst = 'Estado é obrigatório';
    }

    if (!formData.cclass_fiscal || formData.cclass_fiscal.trim() === '') {
      errors.cclass_fiscal = 'Classe Fiscal é obrigatória';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Definir valores padrão para tipo e status ao cadastrar
      const dataToSubmit = {
        ...formData,
        tipo: formData.tipo || 'internet',
        status: formData.status || 'aguardando_instalacao'
      };

      onSubmit(dataToSubmit);
    } else {
      // Mostrar toast de erro de validação
      const errorMessages = Object.values(validationErrors);
      if (errorMessages.length > 0) {
        // Usar toast para mostrar o primeiro erro
        toast.error(`Por favor, corrija os seguintes erros: ${errorMessages[0]}`);
      }
    }
  };

  // Handlers para campos de moeda
  const handleValorChange = (valor: string) => {
    const valorFormatadoStr = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatadoStr);

    setValorFormatado(valorFormatadoStr);
    setFormData({ ...formData, valor: valorNumerico });
    clearFieldError('valor');
  };

  const handleAcrescimoChange = (valor: string) => {
    const valorFormatadoStr = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatadoStr);

    setAcrescimoFormatado(valorFormatadoStr);
    setFormData({ ...formData, acrescimo: valorNumerico });
  };

  const handleDescontoChange = (valor: string) => {
    const valorFormatadoStr = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatadoStr);

    setDescontoFormatado(valorFormatadoStr);
    setFormData({ ...formData, desconto: valorNumerico });
  };

  // Função para lidar com mudança do plano e preencher valor automaticamente
  const handlePlanoChange = (value: number | number[] | null) => {
    const planoId = Array.isArray(value) ? value[0] || 0 : value || 0;

    // Limpar erro de validação do plano
    clearFieldError('plano_id');

    // Se um plano foi selecionado, buscar e preencher o valor
    if (planoId > 0) {
      // Buscar o plano selecionado diretamente do serviço (como no TabbedContractForm)
      const fetchPlanValue = async () => {
        try {
          const plan = await planosService.getById(planoId);
          if (plan) {
            const valorPlano = plan.valor || 0.00;
            setFormData({
              ...formData,
              plano_id: planoId,
              valor: valorPlano,
              desconto: 0,
              acrescimo: 0
            });
            // Atualizar valor formatado
            setValorFormatado(formatarMoeda(valorPlano));
            setDescontoFormatado('0,00');
            setAcrescimoFormatado('0,00');
          } else {
            setFormData({
              ...formData,
              plano_id: planoId,
              desconto: 0,
              acrescimo: 0
            });
            setDescontoFormatado('0,00');
            setAcrescimoFormatado('0,00');
          }
        } catch (error) {
          console.error('Erro ao buscar valor do plano:', error);
          setFormData({
            ...formData,
            plano_id: planoId,
            desconto: 0,
            acrescimo: 0
          });
          setDescontoFormatado('0,00');
          setAcrescimoFormatado('0,00');
        }
      };

      fetchPlanValue();
    } else {
      // Se nenhum plano selecionado, limpar o valor
      setFormData({
        ...formData,
        plano_id: 0,
        valor: 0.00,
        desconto: 0,
        acrescimo: 0
      });
      setValorFormatado('0,00');
      setDescontoFormatado('0,00');
      setAcrescimoFormatado('0,00');
    }
  };

  // Função para copiar endereço do contrato
  const handleUseContractAddress = (checked: boolean) => {
    setUseContractAddress(checked);

    if (checked && contractAddress) {
      setFormData({
        ...formData,
        cep_inst: contractAddress.cep_inst || '',
        cidade_inst: contractAddress.cidade_inst || '',
        estado_inst: contractAddress.estado_inst || '',
        bairro_inst: contractAddress.bairro_inst || '',
        endereco_inst: contractAddress.endereco_inst || '',
        numero_inst: contractAddress.numero_inst || '',
        complemento_inst: contractAddress.complemento_inst || '',
        codigo_municipio: (contractAddress as any).codigo_municipio || '',
        coordenadas_inst: contractAddress.coordenadas_inst || '',
        referencia_inst: contractAddress.referencia_inst || '',
      });
    }
  };

  // Funções para modal do mapa
  const handleOpenMapModal = () => {
    const currentCoords = formData.coordenadas_inst;
    if (currentCoords && currentCoords.includes(',')) {
      const [lat, lng] = currentCoords.split(',').map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        setSelectedLocation({ lat, lng });
      } else {
        setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
      }
    } else {
      setSelectedLocation({ lat: -14.235004, lng: -51.92528 });
    }
    setMapModalOpen(true);
  };

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    const coordString = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    setFormData(prev => ({ ...prev, coordenadas_inst: coordString }));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingServico ? 'Editar Serviço' : 'Adicionar Serviço'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <PlanosSelect
                value={formData.plano_id || null}
                onChange={handlePlanoChange}
                label="Plano"
                required
                placeholder="Selecione um plano"
                onlyActive={true}
                allowClear={true}
                fullWidth
                size="small"
                error={!!validationErrors.plano_id}
                helperText={validationErrors.plano_id}
                escritorio_id={escritorio_id}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <NasSelect
                value={formData.nas_server ? Number(formData.nas_server) : null}
                onChange={(value) => {
                  setFormData({ ...formData, nas_server: value });
                  clearFieldError('nas_server');
                }}
                label="Servidor NAS"
                size="small"
                fullWidth
                required={true}
                error={!!validationErrors.nas_server}
                helperText={validationErrors.nas_server}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Usuário PPPoE"
                value={formData.usuario_pppoe || ''}
                onChange={(e) => {
                  setFormData({ ...formData, usuario_pppoe: e.target.value });
                  clearFieldError('usuario_pppoe');
                }}
                size="small"
                required
                error={!!validationErrors.usuario_pppoe}
                helperText={validationErrors.usuario_pppoe}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Senha PPPoE"
                type="password"
                value={formData.senha_pppoe || ''}
                onChange={(e) => {
                  setFormData({ ...formData, senha_pppoe: e.target.value });
                  clearFieldError('senha_pppoe');
                }}
                size="small"
                required
                error={!!validationErrors.senha_pppoe}
                helperText={validationErrors.senha_pppoe}
                autoComplete="new-password"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Endereço IP"
                value={formatarIp(formData.endereco_ip || '')}
                onChange={(e) => setFormData({ ...formData, endereco_ip: formatarIpOnInput(e.target.value) })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Endereço MAC"
                value={formatarMacAddress(formData.endereco_mac || '')}
                onChange={(e) => setFormData({ ...formData, endereco_mac: formatarMacAddressOnInput(e.target.value) })}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pool de IPs"
                value={formData.pool || ''}
                onChange={(e) => setFormData({ ...formData, pool: e.target.value })}
                size="small"
              />
            </Grid>
            {/* Configurações Adicionais */}
            {/* Permitir Acessos Simultâneos, iniciar desmarcado */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.acessos_simultaneos || false}
                    onChange={(e) => setFormData({ ...formData, acessos_simultaneos: e.target.checked })}
                  />
                }
                label="Permitir Acessos Simultâneos"
              />
            </Grid>

            {/* Seção de Valores */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                Valores
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Valor"
                value={valorFormatado}
                onChange={(e) => handleValorChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                placeholder="0,00"
                size="small"
                required
                error={!!validationErrors.valor}
                helperText={validationErrors.valor}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Desconto"
                value={descontoFormatado}
                onChange={(e) => handleDescontoChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                placeholder="0,00"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Acréscimo"
                value={acrescimoFormatado}
                onChange={(e) => handleAcrescimoChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                placeholder="0,00"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isento || false}
                    onChange={(e) => setFormData({ ...formData, isento: e.target.checked })}
                  />
                }
                label="Isento"
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <ClassFiscalSelect
                value={formData.cclass_fiscal || null}
                onChange={(value) => {
                  setFormData({ ...formData, cclass_fiscal: value ? value.toString() : undefined });
                }}
                label="Classe Fiscal"
                placeholder="Selecione uma classe fiscal"
                allowClear
                fullWidth
                required
                size="small"
                error={!!validationErrors.cclass_fiscal}
                helperText={validationErrors.cclass_fiscal}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                DICI / ANATEL
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Meio</InputLabel>
                <Select
                  value={formData.tipo_meio || 'fibra'}
                  label="Tipo de Meio"
                  onChange={(e) => setFormData({ ...formData, tipo_meio: e.target.value as any })}
                >
                  <MenuItem value="fibra">Fibra Óptica</MenuItem>
                  <MenuItem value="radio">Rádio</MenuItem>
                  <MenuItem value="cabo_coaxial">Cabo Coaxial</MenuItem>
                  <MenuItem value="par_metalico">Par Metálico</MenuItem>
                  <MenuItem value="satelite">Satélite</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Atendimento</InputLabel>
                <Select
                  value={formData.tipo_atendimento || 'urbano'}
                  label="Tipo de Atendimento"
                  onChange={(e) => setFormData({ ...formData, tipo_atendimento: e.target.value as any })}
                >
                  <MenuItem value="urbano">Urbano</MenuItem>
                  <MenuItem value="rural">Rural</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.gera_dici !== undefined ? formData.gera_dici : true}
                    onChange={(e) => setFormData({ ...formData, gera_dici: e.target.checked })}
                  />
                }
                label="Gerar DICI"
              />
            </Grid>

            {/* Seção de Endereço de Instalação */}
            <Grid item xs={12} sx={{ display: 'flex', mb: 2, alignItems: 'flex-end' }} gap={2}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                Endereço de Instalação
              </Typography>
              {/* Checkbox para usar endereço do contrato */}
              {contractAddress && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useContractAddress}
                      onChange={(e) => handleUseContractAddress(e.target.checked)}
                      disabled={!contractAddress || !!editingServico}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Usar endereço do contrato</Typography>}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <TextField
                fullWidth
                label="CEP"
                value={formData.cep_inst || ''}
                onChange={(e) => {
                  setFormData({ ...formData, cep_inst: e.target.value });
                  clearFieldError('cep_inst');
                }}
                placeholder="00000-000"
                size="small"
                disabled={!!editingServico}
                required
                error={!!validationErrors.cep_inst}
                helperText={validationErrors.cep_inst}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.cidade_inst || ''}
                onChange={(e) => {
                  setFormData({ ...formData, cidade_inst: e.target.value });
                  clearFieldError('cidade_inst');
                }}
                size="small"
                disabled={!!editingServico}
                required
                error={!!validationErrors.cidade_inst}
                helperText={validationErrors.cidade_inst}
              />
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <SelectEstadosBr
                value={formData.estado_inst || ''}
                onChange={(e) => {
                  setFormData({ ...formData, estado_inst: e.target.value });
                  clearFieldError('estado_inst');
                }}
                label="Estado"
                disabled={!!editingServico}
                required
                error={!!validationErrors.estado_inst}
                helperText={validationErrors.estado_inst}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Cód. IBGE"
                value={formData.codigo_municipio || ''}
                onChange={(e) => setFormData({ ...formData, codigo_municipio: e.target.value })}
                size="small"
                disabled={!!editingServico}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <BairroSelect
                value={formData.bairro_inst || null}
                onChange={(v) => {
                  setFormData({ ...formData, bairro_inst: v || '' });
                  clearFieldError('bairro_inst');
                }}
                label="Bairro"
                size="small"
                allowCustom
                allowClear={false}
                required
                disabled={!!editingServico}
                error={!!validationErrors.bairro_inst}
                helperText={validationErrors.bairro_inst}
                cidade_inst={formData.cidade_inst}
                estado_inst={formData.estado_inst}
                escritorio_id={escritorio_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.endereco_inst || ''}
                onChange={(e) => {
                  setFormData({ ...formData, endereco_inst: e.target.value });
                  clearFieldError('endereco_inst');
                }}
                size="small"
                disabled={!!editingServico}
                required
                error={!!validationErrors.endereco_inst}
                helperText={validationErrors.endereco_inst}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Número"
                value={formData.numero_inst || ''}
                onChange={(e) => setFormData({ ...formData, numero_inst: e.target.value })}
                size="small"
                disabled={!!editingServico}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Referência"
                value={formData.referencia_inst || ''}
                onChange={(e) => setFormData({ ...formData, referencia_inst: e.target.value })}
                placeholder="Ponto de referência próximo"
                size="small"
                disabled={!!editingServico}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Coordenadas"
                value={formData.coordenadas_inst || ''}
                onChange={(e) => setFormData({ ...formData, coordenadas_inst: e.target.value })}
                placeholder="lat, lng"
                size="small"
                disabled={!!editingServico}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleOpenMapModal}
                        size="small"
                        title="Selecionar no Mapa"
                        disabled={!!editingServico}
                      >
                        <MapIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Complemento"
                value={formData.complemento_inst || ''}
                onChange={(e) => setFormData({ ...formData, complemento_inst: e.target.value })}
                size="small"
                disabled={!!editingServico}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl component="fieldset" size="small">
                <RadioGroup
                  row
                  value={formData.tipo_moradia ?? ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipo_moradia: (e.target.value as any) || undefined }))}
                >
                  <FormControlLabel value="propria" control={<Radio size="small" disabled={!!editingServico} />} label="Própria" />
                  <FormControlLabel value="alugado" control={<Radio size="small" disabled={!!editingServico} />} label="Alugado" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes || ''}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {editingServico ? 'Salvar Alterações' : 'Adicionar Serviço'}
          </Button>
        </DialogActions>
      </Dialog>

      <MapSelectorModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        onConfirm={handleConfirmMapLocation}
        initialCoordinates={selectedLocation}
        title="📍 Selecionar Localização no Mapa"
        description="Clique no mapa para selecionar a localização de instalação do serviço"
      />
    </>
  );
};

export default ContratoServicosIntForm;
